import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';

import { QueryKey } from '@tanstack/react-query';
import { TFunction } from 'i18next';

import { ReactComponent as FolderIcon } from '@/assets/icons/folder-icon.svg';
import AppButton from '@/shared/ui/AppButton';

import { decorationTitle } from '../../model/driveLabels';
import { OrderDriveSummary } from '../../model/types';
import DriveFolderStatus from '../DriveFolderStatus';
import { driveDialogText } from './driveDialogText';

interface Props {
  open: boolean;
  onClose: () => void;
  orderId: string;
  summary: OrderDriveSummary;
  /** A booking's name as the order page shows it. */
  bookingLabel: (bookingId: string) => string;
  canUpload: boolean;
  canManage: boolean;
  alsoInvalidate?: QueryKey[];
}

const cardSx = {
  border: 1,
  borderColor: 'grey.100',
  borderRadius: '10px',
  overflow: 'hidden',
} as const;

const rowSx = {
  px: 2,
  py: 1.5,
  gap: 1.5,
  '& svg': { color: 'accent.main' },
} as const;

const ROW_PAD_X = 16;
const ROW_PAD_Y = 12;
const FOLDER_GLYPH = 18;
/** Enough indent that the elbow has a visible run before the child's own glyph. */
const CHILD_INDENT = 56;
/** Where the parent's folder glyph is centred, which is where the elbow hangs from. */
const ELBOW_X = ROW_PAD_X + FOLDER_GLYPH / 2;

/**
 * A decoration sits under its booking, joined by an elbow dropped from the parent's folder glyph. `::before` is
 * the elbow itself — down to this row's middle, then right — and `::after` carries the stem on down to the next
 * sibling, so a booking with several decorations draws one unbroken line instead of detached ticks. Both are sized
 * with `height` and overlap the neighbouring row by `ROW_PAD_Y` rather than meeting at an exact edge, so no
 * rounding can open a hairline between them.
 */
const childRowSx = {
  ...rowSx,
  pl: `${CHILD_INDENT}px`,
  pt: 0,
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: `${ELBOW_X}px`,
    top: `-${ROW_PAD_Y}px`,
    height: `calc(50% + ${ROW_PAD_Y}px)`,
    width: `${CHILD_INDENT - ELBOW_X - 6}px`,
    borderLeft: '1px solid',
    borderBottom: '1px solid',
    borderColor: 'grey.200',
    borderBottomLeftRadius: '8px',
  },
  '&:not(:last-child)::after': {
    content: '""',
    position: 'absolute',
    left: `${ELBOW_X}px`,
    top: '50%',
    height: `calc(50% + ${ROW_PAD_Y}px)`,
    borderLeft: '1px solid',
    borderColor: 'grey.200',
  },
} as const;

/**
 * The whole order in Drive at a glance: the `00 · Order Files` drop, where files nobody could place land, the order's
 * own links, and every visible booking with its decorations.
 */
const OrderDriveDialog: FC<Props> = ({
  open,
  onClose,
  orderId,
  summary,
  bookingLabel,
  canUpload,
  canManage,
  alsoInvalidate,
}) => {
  const { t } = useTranslation();

  return (
    // Google's Picker opens from here in an iframe outside the dialog; a focus trap would pull focus back from it.
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      disableEnforceFocus
      data-testid="order-drive-dialog"
      PaperProps={{ sx: { borderRadius: '12px' } }}
    >
      <DialogTitle component="div" sx={{ p: 2 }}>
        <Typography sx={driveDialogText.title}>{t('artwork_drive.order_title')}</Typography>
      </DialogTitle>
      <DialogContent sx={{ '&.MuiDialogContent-root': { p: 2 } }}>
        <Stack gap={2}>
          <Stack gap={1}>
            <Typography sx={driveDialogText.section}>
              {t('artwork_drive.order_shared_files')}
            </Typography>
            <Stack sx={cardSx}>
              <Stack direction="row" alignItems="center" sx={rowSx}>
                <FolderIcon width={18} height={18} />
                <Stack flex={1} minWidth={0}>
                  <Typography sx={driveDialogText.primary} noWrap>
                    {t('artwork_drive.drop_title')}
                  </Typography>
                  <Typography sx={driveDialogText.secondary} noWrap>
                    {summary.drop
                      ? folderCounts(t, summary.drop)
                      : t('artwork_drive.not_provisioned')}
                  </Typography>
                </Stack>
                {summary.drop ? (
                  <DriveFolderStatus
                    orderId={orderId}
                    folder={{ kind: 'drop' }}
                    summary={summary.drop}
                    title={t('artwork_drive.drop_title')}
                    canUpload={canUpload}
                    canManage={canManage}
                    alsoInvalidate={alsoInvalidate}
                    presentation="icons"
                  />
                ) : null}
              </Stack>
              <Typography sx={driveDialogText.secondary} px={2} pb={1.5}>
                {t('artwork_drive.drop_hint')}
              </Typography>
            </Stack>
            <Typography sx={driveDialogText.secondary}>
              {t('artwork_drive.order_links', { count: summary.orderLinks })}
            </Typography>
          </Stack>

          <Stack gap={1}>
            <Typography sx={driveDialogText.section}>
              {t('artwork_drive.order_decorations')}
            </Typography>
            <Stack sx={cardSx}>
              {summary.bookings.map((booking, bookingIndex) => (
                <Stack
                  key={booking.bookingId}
                  data-testid="order-drive-booking"
                  sx={bookingIndex > 0 ? { borderTop: 1, borderColor: 'grey.100' } : undefined}
                >
                  <Stack direction="row" alignItems="center" sx={rowSx}>
                    <FolderIcon width={18} height={18} />
                    <Stack flex={1} minWidth={0}>
                      <Typography sx={driveDialogText.primary} noWrap>
                        {bookingLabel(booking.bookingId)}
                      </Typography>
                      <Typography sx={driveDialogText.secondary} noWrap>
                        {folderCounts(t, booking)}
                      </Typography>
                    </Stack>
                    <DriveFolderStatus
                      orderId={orderId}
                      folder={{ kind: 'booking', bookingId: booking.bookingId }}
                      summary={booking}
                      title={bookingLabel(booking.bookingId)}
                      canManage={canManage}
                      alsoInvalidate={alsoInvalidate}
                      presentation="icons"
                    />
                  </Stack>
                  {booking.decorations.map((decoration, index) => (
                    <Stack
                      key={decoration.operationId}
                      direction="row"
                      alignItems="center"
                      sx={childRowSx}
                    >
                      <FolderIcon width={18} height={18} />
                      <Stack flex={1} minWidth={0}>
                        <Typography sx={driveDialogText.primary} noWrap>
                          {decorationTitle(t, index)}
                        </Typography>
                        <Typography sx={driveDialogText.secondary} noWrap>
                          {folderCounts(t, decoration)}
                        </Typography>
                      </Stack>
                      <DriveFolderStatus
                        orderId={orderId}
                        folder={{ kind: 'decoration', operationId: decoration.operationId }}
                        summary={decoration}
                        title={`${bookingLabel(booking.bookingId)} · ${decorationTitle(t, index)}`}
                        canUpload={canUpload}
                        canManage={canManage}
                        alsoInvalidate={alsoInvalidate}
                        presentation="icons"
                      />
                    </Stack>
                  ))}
                </Stack>
              ))}
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 0 }}>
        <AppButton variant="secondary" onClick={onClose}>
          {t('buttons.close')}
        </AppButton>
      </DialogActions>
    </Dialog>
  );
};

/** "N files · M links" under a folder's name, the counts its row no longer carries as a pill. */
const folderCounts = (t: TFunction, folder: { files: number; links: number }): string =>
  [
    t('artwork_drive.file_counts', { files: folder.files }),
    t('artwork_drive.link_counts', { links: folder.links }),
  ].join(' · ');

export default OrderDriveDialog;
